"use client";

import { useEffect, useState } from "react";
import { useRouter, useSearchParams } from "next/navigation";
import Link from "next/link";
import { NovilotMark } from "@/components/brand/NovilotMark";
import { GoogleAuthButton } from "@/components/auth/GoogleAuthButton";
import { PrivacyPreferences } from "@/components/analytics/PrivacyPreferences";
import { MarketingLanguageSwitcher } from "@/components/i18n/MarketingLanguageSwitcher";
import { useI18n } from "@/components/providers/I18nProvider";
import { usePathname } from "next/navigation";
import { localizedMarketingPath, marketingLocaleFromPath } from "@/lib/i18n/marketingLocale";
import { trackGaEvent, trackAdsRegistration } from "@/lib/gtag";
import { capture } from "@/lib/analytics";
import { workspaceIntent, type WorkspaceIntent } from "@/lib/workspaceRouting";
import { billingReturnTo } from "@/lib/authReturnTo";
import {
  clearSignupAttribution,
  readSignupAttribution,
  readMarketingCampaign,
  signupAttributionCaptureProps,
  safeReferrerOrigin,
} from "@/lib/signupAttribution";

export function SignUpForm({ googleEnabled = false, editorCopy, workspaceCopy = {} }: {
  googleEnabled?: boolean;
  workspaceCopy?: Record<string, string>;
  editorCopy?: { title: string; body: string; steps: string[]; cost: string; preview: string };
}) {
  const router = useRouter();
  const searchParams = useSearchParams();
  const next = billingReturnTo(searchParams.get("next"));
  const initialIntent = workspaceIntent(searchParams.get("intent")) ?? "writer";
  const [intent, setIntent] = useState<WorkspaceIntent>(initialIntent);
  useEffect(() => setIntent(initialIntent), [initialIntent]);
  const editor = intent === "editor" ? editorCopy : undefined;
  const text = (source: string) => workspaceCopy[source] ?? source;
  const { t, translateMarketing: localize } = useI18n();
  const pathname = usePathname() || "/sign-up";
  const locale = marketingLocaleFromPath(pathname) ?? "en";
  const localizedPath = (value: string) => localizedMarketingPath(value, locale);

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [ready, setReady] = useState(false);

  // Persist ?src= / ?intent= before Google OAuth drops the query string.
  useEffect(() => {
    readMarketingCampaign();
    readSignupAttribution();
    setReady(true);
  }, []);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!ready) return;
    setError("");

    if (password !== confirm) {
      setError(t("auth.signUp.passwordMismatch"));
      return;
    }
    if (password.length < 8) {
      setError(t("auth.signUp.passwordLength"));
      return;
    }

    setLoading(true);
    const attribution = { ...signupAttributionCaptureProps(), intent };
    trackGaEvent("signup_started", { method: "email", ...attribution });
    capture("signup_started", { method: "email", ...attribution });
    try {
      const { source, intent: campaignIntent, landingAudience } = readSignupAttribution();

      const res = await fetch("/api/auth/sign-up", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email,
          password,
          next,
          name: name.trim() || undefined,
          source,
          landingAudience,
          intent,
          workspaceIntent: intent,
          referrer: safeReferrerOrigin(document.referrer),
        }),
      });
      const data = (await res.json().catch(() => ({}))) as {
        error?: string;
        redirect?: string;
        converted?: boolean;
        conversionId?: string;
      };
      if (!res.ok) {
        setError(typeof data.error === "string" ? data.error : t("auth.signUp.createError"));
        setLoading(false);
        return;
      }
      trackGaEvent("signup_completed", { method: "email", ...attribution });
      capture("signup_completed", { method: "email", ...attribution });
      if (data.conversionId) trackAdsRegistration(data.conversionId);
      clearSignupAttribution();
      // Brand-new accounts go through first-run onboarding. Two exceptions honor
      // the server's redirect instead: a converting guest (they already have the
      // sample workspace they were editing — don't yank them into new-user setup)
      // and an unverified account (must land on /verify-email). The import intent
      // from a landing CTA pre-selects the "work in progress" path.
      const skipOnboarding = data.converted || (data.redirect && data.redirect !== "/dashboard");
      const dest = skipOnboarding
        ? data.redirect || "/dashboard"
        : intent === "editor"
          ? "/editors"
        : intent === "writer" && campaignIntent === "import"
          ? "/onboarding?import=1"
          : "/onboarding";
      router.push(dest);
      router.refresh();
    } catch {
      setError(t("auth.genericError"));
      setLoading(false);
    }
  }

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-background px-6 py-20 gap-6 relative">
      <div className="absolute top-5 end-5">
        <MarketingLanguageSwitcher compact />
      </div>
      <div className="flex items-center gap-2">
        <NovilotMark className="w-6 h-6 text-accent" />
        <span className="text-xl font-semibold">Novilot</span>
      </div>

      <form
        method="post"
        action={localizedPath("/sign-up")}
        onSubmit={onSubmit}
        className="w-full max-w-sm space-y-4 p-6 border border-border rounded-xl bg-card shadow-sm"
      >
        <h1 className="text-lg font-semibold text-center">{next ? t("auth.signUp.checkoutTitle") : editor?.title ?? t("auth.signUp.title")}</h1>
        <noscript>
          <p className="rounded-lg border border-border bg-muted/40 p-3 text-sm text-muted-foreground">
            {localize("Enable JavaScript to create an account securely.")}
          </p>
        </noscript>
        {next && (
          <p className="text-center text-sm text-muted-foreground">{t("auth.signUp.checkoutBody")}</p>
        )}
        <fieldset className="space-y-2" disabled={loading}>
          <legend className="mb-2 text-sm font-medium">{text("How will you use Novilot?")}</legend>
          <div className="grid grid-cols-3 gap-2">
            {([ ["writer", "Writer"], ["editor", "Editor"], ["both", "Both"] ] as const).map(([value, label]) => (
              <label key={value} className={`flex min-w-0 cursor-pointer flex-col items-center justify-center gap-1.5 rounded-lg border px-2 py-2.5 text-sm sm:flex-row ${intent === value ? "border-accent bg-accent/10 text-accent" : "border-border hover:bg-muted"}`}>
                <input type="radio" name="workspaceIntent" value={value} checked={intent === value} onChange={() => setIntent(value)} className="shrink-0 accent-accent" />
                <span className="min-w-0 break-words text-center">{text(label)}</span>
              </label>
            ))}
          </div>
          <p className="text-xs leading-5 text-muted-foreground">{text("You can switch between Writer and Editor from the Novilot menu at any time.")}</p>
        </fieldset>
        {editor && (
          <div className="space-y-3 text-sm leading-6 text-muted-foreground">
            <p className="text-center">{editor.body}</p>
            <ol className="list-decimal space-y-1 ps-5">{editor.steps.map((step) => <li key={step}>{step}</li>)}</ol>
            <p className="rounded-lg bg-muted/40 p-3 text-xs leading-5">{editor.cost}</p>
          </div>
        )}

        {error && (
          <p className="text-sm text-destructive text-center" role="alert" aria-live="polite">
            {error}
          </p>
        )}

        {googleEnabled && (
          <>
            <GoogleAuthButton label={t("auth.signUp.google")} workspaceIntent={intent} next={next} />
            <div className="flex items-center gap-3 text-xs text-muted-foreground">
              <span className="h-px flex-1 bg-border" />
              {t("auth.signIn.or")}
              <span className="h-px flex-1 bg-border" />
            </div>
          </>
        )}

        <div className="space-y-2">
          <label htmlFor="name" className="text-sm font-medium">
            {t("auth.signUp.name")} <span className="text-muted-foreground font-normal">{t("auth.signUp.optional")}</span>
          </label>
          <input
            id="name"
            name="name"
            type="text"
            autoComplete="name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="w-full px-3 py-2 text-sm rounded-lg border border-border bg-background focus:outline-none focus:ring-2 focus:ring-accent/30"
          />
        </div>

        <div className="space-y-2">
          <label htmlFor="email" className="text-sm font-medium">
            {t("auth.signUp.email")}
          </label>
          <input
            id="email"
            name="email"
            type="email"
            dir="ltr"
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            className="w-full px-3 py-2 text-sm rounded-lg border border-border bg-background focus:outline-none focus:ring-2 focus:ring-accent/30"
          />
        </div>

        <div className="space-y-2">
          <label htmlFor="password" className="text-sm font-medium">
            {t("auth.signUp.password")}
          </label>
          <input
            id="password"
            name="password"
            type="password"
            dir="ltr"
            autoComplete="new-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            className="w-full px-3 py-2 text-sm rounded-lg border border-border bg-background focus:outline-none focus:ring-2 focus:ring-accent/30"
          />
        </div>

        <div className="space-y-2">
          <label htmlFor="confirm" className="text-sm font-medium">
            {t("auth.signUp.confirmPassword")}
          </label>
          <input
            id="confirm"
            name="confirm"
            type="password"
            dir="ltr"
            autoComplete="new-password"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            required
            className="w-full px-3 py-2 text-sm rounded-lg border border-border bg-background focus:outline-none focus:ring-2 focus:ring-accent/30"
          />
        </div>

        <button
          type="submit"
          disabled={loading || !ready}
          className="w-full py-2.5 text-sm font-medium rounded-lg bg-accent text-accent-foreground hover:opacity-90 disabled:opacity-50"
        >
          {!ready ? localize("Preparing sign-up…") : loading ? t("auth.signUp.loading") : next ? t("auth.signUp.checkoutSubmit") : editor?.title ?? t("auth.signUp.submit")}
        </button>

        {!next && (
          <p className="text-center text-xs text-muted-foreground">
            {localize("No credit card required.")}
          </p>
        )}

        <p className="text-center text-sm text-muted-foreground">
          {t("auth.signUp.haveAccount")} {" "}
          <Link href={localizedPath(`/sign-in?${new URLSearchParams({ intent, ...(next ? { next } : {}) })}`)} className="text-accent font-medium hover:underline">
            {t("auth.signUp.signIn")}
          </Link>
        </p>

        <p className="text-center text-xs text-muted-foreground">
          {t("auth.signUp.termsPrefix")} {" "}
          <Link href={localizedPath("/terms")} className="underline underline-offset-2 hover:text-foreground">{t("auth.signUp.terms")}</Link>
          {" "}{t("auth.signUp.and")}{" "}
          <Link href={localizedPath("/privacy")} className="underline underline-offset-2 hover:text-foreground">{t("auth.signUp.privacy")}</Link>.
          {" "}{t("auth.signUp.disclaimer")}
        </p>
      </form>
      <PrivacyPreferences inlineNotice />
      {editor && <Link href={localizedPath("/sample-continuity-report")} className="max-w-sm text-center text-sm text-accent hover:underline">{editor.preview}</Link>}
    </div>
  );
}
